import variantSectionUpdater from './unified-variant-section-fetch.js';

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  connectedCallback() {
    this.updateOptions();
    this.updateMasterId();
    if (this.currentVariant) this.updateVariantImage();
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.dispatchVariantChange();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          if (!this.options[index]) {
            return true;
          }
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  dispatchVariantChange() {
    document.dispatchEvent(
      new CustomEvent('variant-change', {
        bubbles: true,
        variant: this.currentVariant,
        detail: {
          variant: this.currentVariant,
        },
      })
    );
  }

  updateMedia() {
    if (!this.currentVariant) return;

    this.updateVariantImage();

    const variantMediaConfig = this.getVariantMediaConfig();
    if (!variantMediaConfig || !variantMediaConfig[this.currentVariant.id]) return;

    const variantMedia = variantMediaConfig[this.currentVariant.id].media;
    if (!variantMedia || variantMedia.length === 0) return;

    const firstVariantMediaId = variantMedia[0].id;

    this.updateDesktopMedia(firstVariantMediaId);
    this.updateMobileMedia(firstVariantMediaId);
  }

  updateVariantImage() {
    const variantMediaConfig = this.getVariantMediaConfig();
    if (!variantMediaConfig || !variantMediaConfig[this.currentVariant.id]) return;

    const currentVariantConfig = variantMediaConfig[this.currentVariant.id];

    this.hideAllVariantImages();

    if (currentVariantConfig.featured_image) {
      this.showVariantImage(currentVariantConfig.featured_image.id);
    }
  }

  hideAllVariantImages() {
    const desktopVariantImages = document.querySelectorAll(
      '[data-product-media-desktop] .product-media__variant-image'
    );
    const mobileVariantSlides = document.querySelectorAll(
      '[data-product-media-mobile] .product-media__variant-slide'
    );

    desktopVariantImages.forEach((image) => {
      image.classList.add('hidden');
    });

    mobileVariantSlides.forEach((slide) => {
      slide.classList.add('hidden');
    });
  }

  showVariantImage(imageId) {
    const desktopVariantImage = document.querySelector(
      `[data-product-media-desktop] [data-media-id="${this.dataset.section}-${imageId}"]`
    );
    const mobileVariantSlide = document.querySelector(
      `[data-product-media-mobile] [data-variant-id="${this.currentVariant.id}"]`
    );

    if (desktopVariantImage) {
      desktopVariantImage.classList.remove('hidden');
      const desktopContainer = document.querySelector('[data-product-media-desktop]');
      if (desktopContainer) {
        desktopContainer.prepend(desktopVariantImage);
        desktopContainer.scrollTo(0, 0);
      }
    }

    if (mobileVariantSlide) {
      mobileVariantSlide.classList.remove('hidden');
      const mobileContainer = document.querySelector('[data-product-media-mobile]');
      if (mobileContainer) {
        mobileContainer.prepend(mobileVariantSlide);

        const swiper = document.querySelector('[data-product-media-swiper]');
        if (swiper && swiper.swiper) {
          swiper.swiper.slideTo(0);
        }
      }
    }
  }

  updateDesktopMedia(mediaId) {
    const desktopContainer = document.querySelector('[data-product-media-desktop]');
    if (!desktopContainer) return;

    const targetMedia = desktopContainer.querySelector(
      `[data-media-id="${this.dataset.section}-${mediaId}"]`
    );
    if (!targetMedia) return;

    desktopContainer.prepend(targetMedia);
    desktopContainer.scrollTo(0, 0);
  }

  updateMobileMedia(mediaId) {
    const mobileContainer = document.querySelector('[data-product-media-mobile]');
    if (!mobileContainer) return;

    const targetMedia = mobileContainer.querySelector(
      `[data-media-id="${this.dataset.section}-${mediaId}"]`
    );
    if (!targetMedia) return;

    mobileContainer.prepend(targetMedia);

    const swiper = document.querySelector('[data-product-media-swiper]');
    if (swiper && swiper.swiper) {
      swiper.swiper.slideTo(0);
    }
  }

  getVariantMediaConfig() {
    const configElement = document.querySelector('[data-variant-media-config]');
    if (!configElement) return null;

    try {
      return JSON.parse(configElement.textContent);
    } catch (error) {
      console.error('Failed to parse variant media config:', error);
      return null;
    }
  }

  updateURL() {
    if (!this.currentVariant) return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
    variantSectionUpdater.updateSections(this.dataset.url, this.currentVariant.id);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  renderProductInfo() {
    document.getElementById(`price-${this.dataset.section}`)?.classList.remove('hidden');
    if (this.currentVariant) {
      this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
    }
  }

  /**
   * toggleAddButton
   * @param {Boolean} disable
   * @param {String} text
   * @param {Boolean} modifyClass
   * @returns {void}
   */
  toggleAddButton(disable = true, text, modifyClass = true) {
    const addButton = document
      .getElementById(`product-form-${this.dataset.section}`)
      ?.querySelector('[name="add"]');
    const addButtonText = addButton.querySelector('[data-add-to-cart-button-text]');

    if (!addButton || !addButtonText) return;

    if (disable) {
      addButton.setAttribute('disabled', true);
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const addButton = document
      .getElementById(`product-form-${this.dataset.section}`)
      ?.querySelector('[name="add"]');
    const addButtonText = addButton?.querySelector('[data-add-to-cart-button-text]');
    if (!addButton || !addButtonText) return;

    addButtonText.textContent = window.variantStrings.unavailable;
    addButton.setAttribute('disabled', true);
    document.getElementById(`price-${this.dataset.section}`)?.classList.add('hidden');
  }

  getVariantData() {
    this.variantData =
      this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects);
}

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }


  connectedCallback() {
    this.updateOptions();
    this.updateMasterId();
    this.filterOptions();
    if (this.currentVariant) {
      this.updateVariantImage();
    }
  }
  onVariantChange() {
    this.filterOptions();
    super.onVariantChange();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
    this.updateSelectedValueLabels(fieldsets);
  }

  updateSelectedValueLabels(fieldsets) {
    fieldsets.forEach((fieldset) => {
      const checkedInput = fieldset.querySelector('input:checked');
      const valueDisplay = fieldset.querySelector('[data-selected-value-display]');
      if (!checkedInput || !valueDisplay) return;

      valueDisplay.textContent =
        checkedInput.dataset.optionValueDisplay || checkedInput.value;
      valueDisplay.parentElement?.classList.remove('hidden');
    });
  }

  filterOptions() {
    const firstOptionGroup = this.querySelector('[data-option-group="0"]');
    const secondOptionGroup = this.querySelector('[data-option-group="1"]');

    if (!firstOptionGroup || !secondOptionGroup) return;

    const selectedFirstOption = firstOptionGroup.querySelector('input:checked');
    if (!selectedFirstOption) return;

    // Capture current variant name before filtering
    const currentSelectedSecondOption = secondOptionGroup.querySelector('input:checked');
    const beforeVariantName = currentSelectedSecondOption
      ? currentSelectedSecondOption.value
      : 'none';

    const firstOptionValue = selectedFirstOption.value;
    const firstOptions = firstOptionGroup.querySelectorAll('[data-option]');
    const secondOptions = secondOptionGroup.querySelectorAll('[data-option]');

    // Reset type option availability state
    firstOptions.forEach((option) => {
      const label = option.querySelector('[data-option-label]');
      if (label) {
        label.classList.remove('opacity-50');
        label.classList.add('!opacity-100');
      }
    });

    // Mark type options as unavailable when all related sizes are sold out
    firstOptions.forEach((option) => {
      const input = option.querySelector('[data-option-input]');
      const typeOptionValue = input.value;
      const matchingVariants = this.getVariantData().filter(
        (variant) => variant.options[0] === typeOptionValue
      );

      if (matchingVariants.length > 0 && matchingVariants.every((variant) => !variant.available)) {
        const label = option.querySelector('[data-option-label]');
        if (label) {
          label.classList.add('opacity-50');
          label.classList.remove('!opacity-100');
        }
      }
    });

    // Show all options first and reset opacity
    secondOptions.forEach((option) => {
      option.style.display = '';
      const label = option.querySelector('[data-option-label]');
      if (label) {
        label.classList.remove('opacity-50');
      }
    });

    // Hide options that don't have matching variants or mark as unavailable
    secondOptions.forEach((option) => {
      const input = option.querySelector('[data-option-input]');
      const secondOptionValue = input.value;

      const matchingVariant = this.getVariantData().find((variant) => {
        return variant.options[0] === firstOptionValue && variant.options[1] === secondOptionValue;
      });

      if (!matchingVariant) {
        option.style.display = 'none';
      } else if (!matchingVariant.available) {
        // Variant exists but is unavailable - add opacity-50 class
        const label = option.querySelector('[data-option-label]');
        if (label) {
          label.classList.add('opacity-50');
        }
      }
    });

    // Check if the previously selected option was hidden
    if (currentSelectedSecondOption) {
      const selectedOptionContainer = currentSelectedSecondOption.closest('[data-option]');
      if (selectedOptionContainer && selectedOptionContainer.style.display === 'none') {
        console.log('❌ Selected variant option was hidden:', beforeVariantName);
        this.selectNearestSizeOption(beforeVariantName, secondOptionGroup);
      }
    }

    // Capture new variant name after filtering
    const newSelectedSecondOption = secondOptionGroup.querySelector('input:checked');
    const afterVariantName = newSelectedSecondOption ? newSelectedSecondOption.value : 'none';

    console.log('🔄 Variant name before filtering:', beforeVariantName);
    console.log('🔄 Variant name after filtering:', afterVariantName);
  }

  selectNearestSizeOption(hiddenOptionValue, secondOptionGroup) {
    // Get all visible options
    const visibleOptions = Array.from(secondOptionGroup.querySelectorAll('[data-option]')).filter(
      (option) => option.style.display !== 'none'
    );

    if (visibleOptions.length === 0) return;

    // Parse the hidden option to get its midpoint
    const hiddenMidpoint = this.parseSizeToMidpoint(hiddenOptionValue);
    if (hiddenMidpoint === null) return;

    // Find the closest visible option
    let closestOption = null;
    let smallestDistance = Infinity;

    visibleOptions.forEach((option) => {
      const input = option.querySelector('[data-option-input]');
      const optionValue = input.value;
      const optionMidpoint = this.parseSizeToMidpoint(optionValue);

      if (optionMidpoint !== null) {
        const distance = Math.abs(optionMidpoint - hiddenMidpoint);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          closestOption = input;
        }
      }
    });

    // Select the closest option
    if (closestOption) {
      closestOption.checked = true;
      console.log('🎯 Selected nearest size option:', closestOption.value);
    }
  }

  parseSizeToMidpoint(sizeString) {
    // Handle range formats like "40-60 cm", "60-80 cm", etc.
    const rangeMatch = sizeString.match(/(\d+)-(\d+)/);
    if (rangeMatch) {
      const start = parseInt(rangeMatch[1]);
      const end = parseInt(rangeMatch[2]);
      return (start + end) / 2;
    }

    // Handle single value formats like "120 cm", "140 cm", etc.
    const singleMatch = sizeString.match(/(\d+)\s*cm/);
    if (singleMatch) {
      return parseInt(singleMatch[1]);
    }

    return null;
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios);
}
